import { ChevronDownOutlineIcon, ChevronUpOutlineIcon } from '@applyboard/ui-icons';
import React, { useContext } from 'react';
import { Box } from '../Box';
import { Button } from '../Button';
import { Text } from '../Text';
import { Timeline } from '../Timeline';
import { useFullTheme } from '~/design';
import {
  assert,
  findChildOfType,
  findChildrenOfTypeRecursively,
  isChildOneOfType,
  useId,
} from '~/utils';
import { Responsive } from '~/design/breakpoint';
import { Flex } from '../Flex';
import { useResponsivePropValue } from '~/utils/useResponsivePropValue';
import { useContextOrThrow } from '~/utils/useContextOrThrow';

type Orientation = 'horizontal' | 'vertical' | 'circular';

export interface StepperProps {
  /** The aria-label of the Stepper. */
  'aria-label'?: string;

  /** The aria-labelledby of the Stepper. Use this if an element outside of the Stepper sufficiently
   * describes the Stepper. */
  'aria-labelledby'?: string;

  /** The content of the Stepper. */
  children: React.ReactNode;

  /** The direction of the steps (default: horizontal). */
  orientation?: Orientation | Responsive<Orientation>;

  /** The current step. */
  activeStep: number;

  // TODO: add test for this
  /**
   * The variant of the active step.
   * - low_saliency - The active step will have a grey icon instead of blue
   * - terminal - The active step will have a negative icon
   */
  activeStepVariant?: 'low_saliency' | 'terminal';
}

const StepperContext = React.createContext<{
  ariaLabel?: StepperProps['aria-label'];
  ariaLabelledby?: StepperProps['aria-labelledby'];
  orientation: StepperProps['orientation'];
  numSteps: number;
  activeStep: StepperProps['activeStep'];
} | null>(null);

const StepContext = React.createContext<{
  didToggleSubSteps?: boolean;
  onToggleSubSteps?: () => void;
}>({});

/**
 * `Stepper` conveys progress of a multi-step flow (e.g. a sign-up wizard) through numbered steps.
 */
export function Stepper({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  children,
  orientation: _orientation = 'horizontal',
  activeStep,
  activeStepVariant,
}: StepperProps) {
  if (!!ariaLabel && !!ariaLabelledby) {
    throw new Error('Only one of aria-label, aria-labelledby can be supplied for Stepper');
  } else if (!ariaLabel && !ariaLabelledby) {
    throw new Error('Provide either an aria-label or aria-labelledby for Stepper');
  }

  const orientation = useResponsivePropValue(_orientation, 'horizontal');

  const [toggledSubStepsMap, setToggledSubStepsMap] = React.useState<Record<number, boolean>>({});

  return (
    <StepperContext.Provider
      value={{
        ariaLabel,
        ariaLabelledby,
        numSteps: React.Children.count(children),
        orientation,
        activeStep,
      }}
    >
      {orientation !== 'circular' ? (
        <Timeline
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          columnGap={orientation === 'vertical' ? 4 : 2}
          orientation={orientation}
          rowGap={orientation === 'horizontal' ? 2 : undefined}
          value={activeStep}
        >
          {React.Children.map(children, (child, index) => {
            const childProps = (child as React.ReactElement<StepProps>)?.props;
            const didToggleSubSteps = toggledSubStepsMap[index];

            const stepStatus =
              activeStep > index ? 'completed' : activeStep === index ? 'in_progress' : 'planned';

            const stepIcon = {
              completed: 'check' as const,
              in_progress:
                activeStepVariant === 'terminal' ? ('no-entry' as const) : ('chevron' as const),
              planned: 'circle' as const,
            }[stepStatus];

            const stepIconIntent = {
              completed: 'positive' as const,
              in_progress:
                activeStepVariant === 'terminal'
                  ? ('negative' as const)
                  : activeStepVariant === 'low_saliency'
                    ? ('secondary' as const)
                    : ('primary' as const),
              planned: 'secondary' as const,
            }[stepStatus];

            const subSteps = findChildrenOfTypeRecursively(<>{child}</>, [Step.SubStep]);
            if (subSteps.length > 0) {
              assert(orientation === 'vertical', 'SubStep is only supported in vertical Stepper');
            }

            const firstSubStepStatus =
              subSteps.length === 0
                ? 'n/a'
                : getSubStepStatus(0, { activeSubStep: childProps.activeSubStep, stepStatus });
            const stepIntent =
              stepStatus === 'completed' ||
              (didToggleSubSteps && firstSubStepStatus === 'completed')
                ? 'positive'
                : 'secondary';

            const ImgElement = childProps.img;

            return (
              <>
                <Timeline.Item
                  intent={stepIntent}
                  key={index}
                  pb={didToggleSubSteps && subSteps.length > 0 ? 6 : undefined}
                >
                  <Timeline.Item.Dot
                    align="start"
                    icon={stepIcon}
                    intent={stepIconIntent}
                    variant={
                      stepStatus === 'planned' ||
                      (stepStatus === 'in_progress' && activeStepVariant === 'low_saliency')
                        ? 'outline'
                        : 'fill'
                    }
                  />
                  {!!ImgElement && (
                    <Timeline.Item.Opposite>
                      <ImgElement />
                    </Timeline.Item.Opposite>
                  )}
                  <StepContext.Provider
                    value={{
                      didToggleSubSteps,
                      onToggleSubSteps:
                        subSteps.length > 0
                          ? () => {
                              setToggledSubStepsMap({
                                ...toggledSubStepsMap,
                                [index]: !toggledSubStepsMap[index],
                              });
                            }
                          : undefined,
                    }}
                  >
                    <div css={{ textAlign: orientation === 'vertical' ? undefined : 'center' }}>
                      {child}
                    </div>
                  </StepContext.Provider>
                </Timeline.Item>
                {didToggleSubSteps &&
                  subSteps.map((subStep, subStepIndex) => {
                    const subStepStatus = getSubStepStatus(subStepIndex, {
                      activeSubStep: childProps.activeSubStep,
                      stepStatus,
                    });

                    return (
                      <Timeline.Item
                        key={`${index}-${subStepIndex}`}
                        intent={subStepStatus === 'completed' ? 'positive' : 'secondary'}
                        pb={subStepIndex === subSteps.length - 1 ? undefined : 6}
                      >
                        <Timeline.Item.Dot
                          align="start"
                          icon="circle"
                          size="xs"
                          variant={subStepStatus === 'completed' ? 'fill' : 'outline'}
                          intent={subStepStatus === 'completed' ? 'positive' : 'secondary'}
                        />
                        <div>{subStep}</div>
                      </Timeline.Item>
                    );
                  })}
              </>
            );
          })}
        </Timeline>
      ) : (
        React.Children.toArray(children)[activeStep]
      )}
    </StepperContext.Provider>
  );
}

function getSubStepStatus(
  subStepIndex: number,
  {
    activeSubStep,
    stepStatus,
  }: { activeSubStep?: number; stepStatus: 'planned' | 'completed' | 'in_progress' },
) {
  if (stepStatus === 'completed') {
    return 'completed';
  }

  if (typeof activeSubStep === 'number' && subStepIndex < activeSubStep) {
    return 'completed';
  }

  return 'planned';
}

export interface StepProps {
  /** The content of the step. */
  children: React.ReactNode;

  /** If true, the step appears as inactive. */
  disabled?: boolean;

  /** The spot illustration to go above or beside the step's dot. */
  img?: React.ComponentType;

  /** The current sub-step  */
  activeSubStep?: number;
}

// This function doesn't actually need to be exported, but for some reason, typescript is
// complaining about it "being private" if it's not exported
export function Step({ children, img }: StepProps) {
  const { orientation, ariaLabel, ariaLabelledby, numSteps, activeStep } = useContextOrThrow(
    StepperContext,
    'Stepper.Step must be used within Stepper',
  );

  const titleChild = findChildOfType(
    children,
    Step.Title,
  ) as unknown as React.ReactElement<StepTitleProps>;
  const labelChild = findChildOfType(
    children,
    Step.Label,
  ) as unknown as React.ReactElement<StepLabelProps>;
  const expandableChild = findChildOfType(children, Step.Expandable);
  const otherChildren = React.Children.toArray(children).filter(
    child => !isChildOneOfType(child, [Step.Label, Step.Expandable, Step.Title, Step.SubStep]),
  );

  if (img) {
    throw new Error('`img` prop for Stepper.Step is not yet supported');
  }

  const numElements = [titleChild, labelChild, expandableChild, !!otherChildren.length].filter(
    Boolean,
  ).length;

  const mobileFlexAlign =
    numElements >= 3
      ? 'start'
      : expandableChild
        ? 'start'
        : otherChildren.length
          ? 'start'
          : 'center';

  return (
    <>
      {orientation !== 'circular' ? (
        <>
          {titleChild}
          {labelChild}
          {expandableChild}
          {!!otherChildren && <Box pt={orientation === 'horizontal' ? 2 : 0}>{otherChildren}</Box>}
        </>
      ) : (
        <Flex align={mobileFlexAlign} gap={4}>
          <ProgressAmount
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            total={numSteps}
            value={activeStep + 1}
          />
          <Box>
            {titleChild}
            {labelChild}
            {!!expandableChild && (
              <Flex direction="column" gap={2} pt={!titleChild && !labelChild ? 3 : 0}>
                {expandableChild}
              </Flex>
            )}
            {!!otherChildren.length && <Box pt={2}>{otherChildren}</Box>}
          </Box>
        </Flex>
      )}
    </>
  );
}

Stepper.Step = Step;

export interface StepLabelProps {
  /** The content for the step description. */
  children: React.ReactNode;

  /** The variant of the label's text. */
  variant?: 'bodyM' | 'bodyS' | 'labelM';
}

Step.Label = function StepLabel({ children, variant = 'bodyS' }: StepLabelProps) {
  const { orientation } = useContextOrThrow(
    StepperContext,
    'Stepper.Step.Label must be used within Stepper.Step',
  );
  const { didToggleSubSteps, onToggleSubSteps } = useContext(StepContext);

  return (
    <Flex
      py={orientation === 'horizontal' ? 2 : 0}
      align="center"
      justify={orientation === 'horizontal' ? 'center' : undefined}
      gap={1}
    >
      <Text
        align={
          orientation === 'circular' ? 'start' : orientation === 'vertical' ? 'start' : 'center'
        }
        variant={variant}
      >
        {children}
      </Text>
      {onToggleSubSteps && (
        <div style={{ display: 'flex', alignItems: 'center', height: 0 }}>
          <Button
            aria-label="Toggle sub steps"
            emphasis="transparent"
            leadIcon={didToggleSubSteps ? ChevronUpOutlineIcon : ChevronDownOutlineIcon}
            onClick={onToggleSubSteps}
            size="sm"
            aria-expanded={didToggleSubSteps}
          />
        </div>
      )}
    </Flex>
  );
};

export interface StepTitleProps {
  /** The content for the step label. */
  children: string;

  /** The variant for the label. */
  variant?: 'titleS' | 'titleM' | 'titleL';
}

Step.Title = function StepTitle({ children, variant = 'titleS' }: StepTitleProps) {
  const { orientation } = useContextOrThrow(
    StepperContext,
    'Stepper.Step.Title must be used within Stepper.Step',
  );

  return (
    <Box pt={2}>
      <Text
        align={
          orientation === 'circular' ? 'start' : orientation === 'vertical' ? 'start' : 'center'
        }
        variant={variant}
      >
        {children}
      </Text>
    </Box>
  );
};

export interface StepExpandableProps {
  /** The content that appears when the step is expanded. */
  children: React.ReactNode;

  /** If true, the step cannot be expanded/collapsed. */
  disabled?: boolean;

  /** The text for the expand button. */
  label: string;

  /** A callback function that is invokved when the step is expanded/collapsed. */
  onClick?: (isExpanded: boolean) => void;

  /** The variant of the label's text. */
  variant?: 'bodyS' | 'labelM';
}

Step.Expandable = function StepExpandable({
  children,
  disabled,
  label,
  onClick,
  variant = 'bodyS',
}: StepExpandableProps) {
  const { orientation } = useContextOrThrow(
    StepperContext,
    'Stepper.Step.Expandable must be used within Stepper.Step',
  );
  const { borders, spacing } = useFullTheme();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const contentId = useId();

  return (
    <>
      <Flex align="center" gap={1} justify={orientation === 'vertical' ? 'start' : 'center'}>
        <Text variant={variant}>{label}</Text>

        <Button
          aria-controls={contentId}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Hide additional content' : 'Show additional content'}
          disabled={disabled}
          emphasis="transparent"
          leadIcon={isExpanded ? ChevronUpOutlineIcon : ChevronDownOutlineIcon}
          onClick={() => {
            setIsExpanded(!isExpanded);
            onClick?.(!isExpanded);
          }}
          size="sm"
        />
      </Flex>

      {isExpanded && (
        <div
          css={{
            borderBottomColor: borders.inactive.borderColor,
            borderBottomStyle: borders.inactive.borderStyle,
            borderBottomWidth: borders.inactive.borderWidth,
            borderTopColor: borders.inactive.borderColor,
            borderTopStyle: borders.inactive.borderStyle,
            borderTopWidth: borders.inactive.borderWidth,
            display: 'inline-block',
            marginTop: spacing[2],
            paddingBlock: spacing[2],
            width: 'fit-content',
          }}
          id={contentId}
        >
          {children}
        </div>
      )}
    </>
  );
};

function ProgressAmount({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  total,
  value,
}: {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  total: number;
  value: number;
}) {
  const percentageValue = Math.min(100 * (value / total), 100);
  const { colors } = useFullTheme();
  const width = 6;
  const sizeInPx = 64;

  // Subtract the stroke width
  const radius = sizeInPx / 2 - width / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentageValue / 100) * circumference;

  return (
    <div
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      css={{ flexShrink: 0, height: sizeInPx, width: sizeInPx }}
      role="region"
    >
      <svg
        aria-label={`Step ${value} out of ${total}`}
        height={sizeInPx}
        role="img"
        width={sizeInPx}
      >
        <circle
          strokeWidth={width}
          fill="transparent"
          stroke={colors.borderRegular}
          r={radius}
          cx={sizeInPx / 2}
          cy={sizeInPx / 2}
        />
        <circle
          css={[
            {
              strokeDasharray: `${circumference} ${circumference}`,
              strokeDashoffset: offset,
              transform: 'rotate(270deg)',
              transformOrigin: '50% 50%',
              transition: 'stroke-dashoffset 0.35s',
            },
          ]}
          cx={sizeInPx / 2}
          cy={sizeInPx / 2}
          fill="transparent"
          r={radius}
          stroke={colors.actionPrimary}
          strokeWidth={width}
        />

        <text
          css={{ color: colors.textSecondary, fontFamily: 'Open Sans' }}
          x="50%"
          y="52%"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {value}/{total}
        </text>
      </svg>
    </div>
  );
}

Step.SubStep = function SubStep({ children }: StepLabelProps) {
  const { orientation } = useContextOrThrow(
    StepperContext,
    'Stepper.Step.Label must be used within Stepper.Step',
  );

  assert(orientation === 'vertical', 'SubStep is only supported in vertical Stepper');

  return <Text variant="bodyS">{children}</Text>;
};
